
import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
// Eliminamos input-otp, usaremos un input nativo
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';


export default function OtpLoginPage() {
  const location = useLocation();
  const initialEmail = (location.state && location.state.email) || '';
  const [email, setEmail] = useState(initialEmail);
    // Nunca aplicar estilos personalizados en esta página
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(!!initialEmail);
  const [loginError, setLoginError] = useState(false);

  // Submit automático solo cuando otp tiene 6 dígitos y showOtp está activo
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  // Evita múltiples envíos automáticos cuando el usuario ingresa los 6 dígitos
  const pendingAutoSubmitRef = useRef(false);

  useEffect(() => {
    if (showOtp && otp.length === 6 && !loading) {
      // Usar setTimeout para asegurar que el valor de otp esté sincronizado antes del submit
      // Evitar programar múltiples requestSubmit si ya hay uno pendiente
      if (pendingAutoSubmitRef.current) return;
      pendingAutoSubmitRef.current = true;
      setTimeout(() => {
        // Solo submit si el valor sigue siendo válido
        if (formRef.current && otp.length === 6) {
          formRef.current.requestSubmit();
        } else {
          // Si no se envió, limpiar el flag
          pendingAutoSubmitRef.current = false;
        }
      }, 0);
    }
  }, [otp, showOtp, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Marcar que estamos procesando un envío (auto o manual) para evitar duplicados
    pendingAutoSubmitRef.current = true;
    if (!showOtp) {
      // Enviar código
      setLoading(true);
      const { error } = await supabase.auth.signInWithOtp({ email });
      setLoading(false);
      if (error) {
        toast({
          title: 'Error al enviar el código',
          description: error.message,
          variant: 'destructive',
        });
        pendingAutoSubmitRef.current = false;
      } else {
        setShowOtp(true);
        setOtp('');
        pendingAutoSubmitRef.current = false;
      }
      return;
    }
    // Verificar OTP solo si tiene 6 dígitos
    if (otp.length !== 6) {
      toast({
        title: 'Código incompleto',
        description: 'El código tiene que tener 6 dígitos.',
        variant: 'destructive',
      });
      return;
    }
    setLoading(true);
    setLoginError(false);
    const { error } = await supabase.auth.verifyOtp({
      email,
      token: otp,
      type: 'email',
    });
    setLoading(false);
    if (error) {
      // Resetear el flag antes de limpiar el input para que el toast vuelva a mostrarse si el usuario se equivoca varias veces
      pendingAutoSubmitRef.current = false;
      setOtp(''); // Limpiar el input si el token es inválido
      setLoginError(true);
    } else {
      setLoginError(false);
      pendingAutoSubmitRef.current = false;
      window.location.href = '/notas';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader>
          <CardTitle>Ingresá tu correo electrónico para recibir un código de acceso.</CardTitle>
          <CardDescription>
            Te vamos a mandar un código de acceso a tu correo electrónico.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form ref={formRef} onSubmit={handleSubmit} className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setShowOtp(false);
                  setOtp('');
                }}
                required
                autoComplete="email"
              />
            </div>
            {showOtp && (
              <div className="grid gap-2">
                <Label htmlFor="otp">Código</Label>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className={cn(
                    "w-full tracking-widest text-center text-2xl rounded-lg border border-input bg-background shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all",
                    loginError && "border-red-500 ring-2 ring-red-500 animate-shake"
                  )}
                  value={otp}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(val);
                    if (loginError) setLoginError(false);
                  }}
                  autoFocus
                  required
                />
                <div className="text-xs text-muted-foreground text-center mt-2">Recibiste un código de 6 dígitos en {email}</div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    className="flex-1 px-3 py-1.5 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors"
                    onClick={() => {
                      setShowOtp(false);
                      setOtp('');
                    }}
                  >
                    Cambiar correo
                  </button>
                  <button
                    type="button"
                    className="flex-1 px-3 py-1.5 text-xs font-medium rounded-md border border-input bg-background hover:bg-accent transition-colors disabled:opacity-50"
                    onClick={async () => {
                      setLoading(true);
                      try {
                        const { error } = await supabase.auth.signInWithOtp({
                          email,
                          options: {
                            shouldCreateUser: true,
                            data: {}
                          }
                        });
                        if (error) throw error;
                        setOtp('');
                      } catch (error: any) {
                        const rawMsg: string = error?.message || '';
                        const friendlyMsg = rawMsg.toLowerCase().includes('security purposes') || rawMsg.toLowerCase().includes('only request this after')
                          ? `Por seguridad, solo podés pedirlo dentro de ${rawMsg.match(/\d+/)?.[0] ?? 'unos'} segundos.`
                          : rawMsg || 'Intenta más tarde';
                        toast({
                          title: 'Error al reenviar código',
                          description: friendlyMsg,
                          variant: 'destructive',
                        });
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    Reenviar
                  </button>
                </div>
              </div>
            )}
            {/* Mostrar el botón solo si NO está en modo OTP */}
            {!showOtp && (
              <Button
                type="submit"
                className="w-full"
                disabled={loading || (showOtp && otp.length !== 6)}
              >
                {loading ? 'Enviando...' : 'Enviar código'}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
